import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDate, formatQty } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';
import type { Article, Location, Warehouse } from '../types';

interface Suggestion {
  to_location_id: string;
  to_code: string;
  article_id: string;
  sku: string;
  article_name: string;
  unit: string;
  owner_name: string;
  current_qty: number;
  replenish_min: number;
  replenish_target: number;
  needed: number;
  from_location_id: string | null;
  from_code: string | null;
  lot_code: string | null;
  available: number;
}

interface Task {
  id: string;
  status: string;
  sku: string;
  article_name: string;
  unit: string;
  owner_name: string;
  from_code: string;
  to_code: string;
  lot_code: string | null;
  qty: number;
  qty_done: number;
  created_at: string;
}

/**
 * Nachschub: Kommissionierplätze aus der Reserve auffüllen. Der
 * Kommissionierer läuft dadurch nur in einer kleinen Zone und steht nie vor
 * einem leeren Platz.
 */
export function ReplenishmentPage() {
  const { can } = useAuth();
  const mayWork = can('admin', 'schichtleiter', 'lagerist', 'disponent');
  const mayConfigure = can('admin', 'kaufmann', 'disponent');

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [pickFaces, setPickFaces] = useState<Location[]>([]);

  const [showSetup, setShowSetup] = useState(false);
  const [setup, setSetup] = useState({ locationCode: '', articleTerm: '', min: '', target: '' });
  const [articleHits, setArticleHits] = useState<Article[]>([]);
  const [chosenArticle, setChosenArticle] = useState<Article | null>(null);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [data, locations] = await Promise.all([
        api<{ tasks: Task[]; suggestions: Suggestion[] }>('/api/warehouse/replenishment', {
          query: { warehouseId },
        }),
        api<Location[]>('/api/warehouse/locations', { query: { warehouseId } }),
      ]);
      setTasks(data.tasks);
      setSuggestions(data.suggestions);
      setPickFaces(locations.filter((location) => location.is_pick_face));
    } catch (err) {
      setError(errorText(err));
    }
  }, [warehouseId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api<Warehouse[]>('/api/warehouse').then(setWarehouses).catch(() => setWarehouses([]));
  }, []);

  useEffect(() => {
    if (!setup.articleTerm.trim()) {
      setArticleHits([]);
      return;
    }
    const timer = setTimeout(() => {
      api<Article[]>('/api/articles/lookup', {
        query: { q: setup.articleTerm.trim(), stockableOnly: 'true' },
      })
        .then(setArticleHits)
        .catch(() => setArticleHits([]));
    }, 220);
    return () => clearTimeout(timer);
  }, [setup.articleTerm]);

  async function plan() {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ created: number; skipped: { to_code: string; reason: string }[] }>(
        '/api/warehouse/replenishment/plan',
        { method: 'POST', body: { warehouseId: warehouseId || undefined } },
      );
      setMessage(
        result.created > 0
          ? `${result.created} Nachschubauftrag/-aufträge eingeplant.` +
              (result.skipped.length > 0 ? ` ${result.skipped.length} übersprungen.` : '')
          : 'Nichts einzuplanen.',
      );
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function complete(task: Task) {
    setBusy(true);
    setError('');
    try {
      await api(`/api/warehouse/replenishment/${task.id}/complete`, {
        method: 'POST',
        body: { movementId: crypto.randomUUID() },
      });
      setMessage(`${formatQty(task.qty, task.unit)} von ${task.from_code} nach ${task.to_code} umgelagert.`);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function cancel(task: Task) {
    setBusy(true);
    try {
      await api(`/api/warehouse/replenishment/${task.id}/cancel`, { method: 'POST' });
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function savePickFace() {
    if (!chosenArticle) return setError('Bitte einen Artikel wählen.');
    const location = pickFacesLookup(setup.locationCode);
    if (!location) return setError(`Lagerplatz ${setup.locationCode} ist unbekannt.`);

    setBusy(true);
    setError('');
    try {
      await api(`/api/warehouse/locations/${location.id}/pick-face`, {
        method: 'PUT',
        body: {
          isPickFace: true,
          fixedArticleId: chosenArticle.id,
          replenishMin: Number(setup.min.replace(',', '.')),
          replenishTarget: setup.target ? Number(setup.target.replace(',', '.')) : null,
        },
      });
      setMessage(`${location.code} ist jetzt Kommissionierplatz für ${chosenArticle.name}.`);
      setShowSetup(false);
      setSetup({ locationCode: '', articleTerm: '', min: '', target: '' });
      setChosenArticle(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const [allLocations, setAllLocations] = useState<Location[]>([]);
  useEffect(() => {
    api<Location[]>('/api/warehouse/locations').then(setAllLocations).catch(() => setAllLocations([]));
  }, [message]);

  function pickFacesLookup(code: string): Location | undefined {
    return allLocations.find((entry) => entry.code.toUpperCase() === code.trim().toUpperCase());
  }

  async function removePickFace(location: Location) {
    setBusy(true);
    try {
      await api(`/api/warehouse/locations/${location.id}/pick-face`, {
        method: 'PUT',
        body: { isPickFace: false },
      });
      setMessage(`${location.code} ist kein Kommissionierplatz mehr.`);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="toolbar">
        <label className="field">
          <span>Lager</span>
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">alle Lager</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.code} · {warehouse.name}
              </option>
            ))}
          </select>
        </label>
        <span className="spacer" />
        {mayWork && (
          <button className="primary" onClick={plan} disabled={busy}>
            Nachschub planen
          </button>
        )}
        {mayConfigure && (
          <button onClick={() => setShowSetup(!showSetup)}>
            {showSetup ? 'Abbrechen' : 'Kommissionierplatz einrichten'}
          </button>
        )}
      </div>

      <Notice kind="info">
        Nachschub wird zusätzlich stündlich automatisch geplant. Ohne eingerichtete
        Kommissionierplätze passiert nichts — ein chaotischer Platz hat keine Sollmenge, die
        unterschritten werden könnte.
      </Notice>

      {showSetup && (
        <Card title="Kommissionierplatz einrichten">
          <div className="form-grid">
            <label className="field">
              <span>Lagerplatz</span>
              <input
                list="all-location-codes"
                value={setup.locationCode}
                onChange={(e) => setSetup({ ...setup, locationCode: e.target.value.toUpperCase() })}
                placeholder="z. B. K-01"
              />
              <datalist id="all-location-codes">
                {allLocations.map((location) => (
                  <option key={location.id} value={location.code} />
                ))}
              </datalist>
            </label>
            <TextField
              label="Mindestmenge"
              value={setup.min}
              onChange={(v) => setSetup({ ...setup, min: v })}
              hint="darunter wird nachgefüllt"
            />
            <TextField
              label="Sollmenge (optional)"
              value={setup.target}
              onChange={(v) => setSetup({ ...setup, target: v })}
              hint="leer = doppelte Mindestmenge"
            />
          </div>

          {chosenArticle ? (
            <div className="chosen">
              <div>
                <strong>{chosenArticle.name}</strong>
                <div className="small mono">{chosenArticle.sku}</div>
              </div>
              <span className="spacer" />
              <button className="small" onClick={() => setChosenArticle(null)}>
                wechseln
              </button>
            </div>
          ) : (
            <>
              <label className="field">
                <span>Fester Artikel für diesen Platz</span>
                <input
                  value={setup.articleTerm}
                  onChange={(e) => setSetup({ ...setup, articleTerm: e.target.value })}
                  placeholder="Artikelnummer oder Bezeichnung"
                />
              </label>
              {articleHits.length > 0 && (
                <div className="pick-list">
                  {articleHits.map((hit) => (
                    <button
                      key={hit.id}
                      onClick={() => {
                        setChosenArticle(hit);
                        setArticleHits([]);
                        setSetup((current) => ({ ...current, articleTerm: '' }));
                      }}
                    >
                      <strong>{hit.name}</strong>
                      <div className="small muted mono">{hit.sku}</div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          <button className="primary" onClick={savePickFace} disabled={busy}>
            Platz einrichten
          </button>
        </Card>
      )}

      <Card title="Offene Nachschubaufträge" padded={false}>
        {!tasks ? (
          <Loading />
        ) : tasks.length === 0 ? (
          <Empty>Kein Nachschub offen.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Von</th>
                <th>Nach</th>
                <th>Artikel</th>
                <th>Charge</th>
                <th className="num">Menge</th>
                <th>Seit</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id}>
                  <td className="mono" style={{ fontWeight: 600 }}>
                    {task.from_code}
                  </td>
                  <td className="mono" style={{ fontWeight: 600 }}>
                    {task.to_code}
                  </td>
                  <td>
                    <span className="mono">{task.sku}</span> {task.article_name}
                  </td>
                  <td className="small">{task.lot_code ?? '—'}</td>
                  <td className="num" style={{ fontSize: 15, fontWeight: 600 }}>
                    {formatQty(task.qty - task.qty_done, task.unit)}
                  </td>
                  <td className="small muted">{formatDate(task.created_at.slice(0, 10))}</td>
                  <td className="num">
                    {mayWork && (
                      <>
                        <button className="primary small" onClick={() => complete(task)} disabled={busy}>
                          erledigt
                        </button>
                        <button className="small ghost" onClick={() => cancel(task)} disabled={busy}>
                          verwerfen
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="grid two">
        <Card title="Unterdeckte Plätze" padded={false}>
          {suggestions.length === 0 ? (
            <Empty>Alle Kommissionierplätze sind ausreichend gefüllt.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Platz</th>
                  <th>Artikel</th>
                  <th className="num">Bestand</th>
                  <th className="num">Minimum</th>
                  <th className="num">Bedarf</th>
                  <th>Quelle</th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map((entry) => (
                  <tr key={entry.to_location_id}>
                    <td className="mono">{entry.to_code}</td>
                    <td className="small">
                      <span className="mono">{entry.sku}</span> {entry.article_name}
                    </td>
                    <td className="num" style={{ color: 'var(--bad)' }}>
                      {formatQty(entry.current_qty)}
                    </td>
                    <td className="num muted">{formatQty(entry.replenish_min)}</td>
                    <td className="num">{formatQty(entry.needed)}</td>
                    <td className="small">
                      {entry.from_code ? (
                        <span className="mono">{entry.from_code}</span>
                      ) : (
                        <Badge kind="bad">keine Reserve</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Eingerichtete Kommissionierplätze" padded={false}>
          {pickFaces.length === 0 ? (
            <Empty>Noch kein Kommissionierplatz eingerichtet.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Platz</th>
                  <th>Fester Artikel</th>
                  <th className="num">Min</th>
                  <th className="num">Soll</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pickFaces.map((location) => (
                  <tr key={location.id}>
                    <td className="mono">{location.code}</td>
                    <td className="small">{location.fixed_article_name ?? '—'}</td>
                    <td className="num">{formatQty(location.replenish_min ?? 0)}</td>
                    <td className="num muted">
                      {location.replenish_target ? formatQty(location.replenish_target) : '—'}
                    </td>
                    <td className="num">
                      {mayConfigure && (
                        <button
                          className="small ghost"
                          onClick={() => removePickFace(location)}
                          disabled={busy}
                        >
                          aufheben
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}
